import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { Table, Button, Modal, message, Space, Typography } from "antd";
import ProductForm from "./ProductForm";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

const { Title } = Typography;

const ProductManagement = ({ companyId }) => {
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [companyId]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/products/company/${companyId}`,
        { headers: { Authorization: token } }
      );
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      message.error("Failed to fetch products");
    }
  };

  const handleAdd = () => {
    setSelectedProduct(null);
    setShowForm(true);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    Modal.confirm({
      title: "Are you sure you want to delete this product and all its offers?",
      onOk: async () => {
        try {
          const token = localStorage.getItem("token");
          await axios.delete(
            `${API_BASE_URL}/products/company/${companyId}/${productId}`,
            { headers: { Authorization: token } }
          );
          fetchProducts();
          message.success("Product deleted successfully");
        } catch (error) {
          console.error("Error deleting product:", error);
          message.error("Failed to delete product");
        }
      },
    });
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const url = selectedProduct
        ? `/products/company/${companyId}/${selectedProduct.id}`
        : `/products/company/${companyId}`;
      const method = selectedProduct ? "put" : "post";
      const token = localStorage.getItem("token");
      await axios[method](API_BASE_URL + url, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });
      setShowForm(false);
      fetchProducts();
      message.success(
        `Product ${selectedProduct ? "updated" : "added"} successfully`
      );
    } catch (error) {
      console.error("Error saving product:", error);
      message.error(`Failed to ${selectedProduct ? "update" : "add"} product`);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price) => `₹${parseFloat(price).toFixed(2)}`,
    },
    {
      title: "Short Description",
      dataIndex: "short_description",
      key: "short_description",
    },
    {
      title: "Actions",
      align: "center",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined style={{ color: "green" }} />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            icon={<DeleteOutlined style={{ color: "red" }} />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space
        style={{
          margin: 16,
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Title level={5}>Products</Title>
        <Button type="primary" size="small" onClick={handleAdd}>
          Add Product
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        pagination={products.length > 10 ? { pageSize: 5 } : false}
        loading={loading}
        locale={{ emptyText: "No products added yet." }}
      />
      <Modal
        title={selectedProduct ? "Edit Product" : "Add Product"}
        open={showForm}
        onCancel={() => setShowForm(false)}
        footer={null}
        destroyOnClose
      >
        <ProductForm
          companyId={companyId}
          product={selectedProduct}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          loading={loading}
        />
      </Modal>
    </div>
  );
};

export default ProductManagement;
